import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { getApiBaseUrl } from '../config';
import './Download.css';

function Download() {
  const navigate = useNavigate();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    address: '',
    os: '',
    osOther: '',
    useCase: '',
    timeline: '',
    captcha: ''
  });
  const [captchaToken, setCaptchaToken] = useState(null);
  const [requestedProduct, setRequestedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalOS, setModalOS] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const osOptions = ['Linux', 'Windows', 'Android', 'iOS', 'macOS', 'PiOS', 'Embedded / Bare Metal', 'Other'];
  const supportedOSOptions = ['Android', 'Linux', 'PiOS', 'macOS'];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e, productType) => {
    e.preventDefault();

    const formElement = e.target.closest('form');
    if (!formElement.checkValidity()) {
      formElement.reportValidity();
      return;
    }

    if (!captchaToken) {
      alert('Please complete the CAPTCHA verification.');
      return;
    }

    setRequestedProduct(productType);

    if (productType === 'pryme' && !supportedOSOptions.includes(formData.os)) {
      // Send initial submit to capture original selection (e.g. Windows)
      await submitToBackend(productType, formData.os, { isInitial: true });
      setShowModal(true);
      return;
    }

    await submitToBackend(productType, formData.os, { isInitial: false });
  };

  const handleModalSubmit = async () => {
    if (!modalOS) {
      alert('Please select an OS');
      return;
    }
    const originalOS = formData.os;
    setFormData(prev => ({ ...prev, os: modalOS }));
    setShowModal(false);
    await submitToBackend('pryme', modalOS, { isUpdate: true, originalOS });
  };

  const submitToBackend = async (productType, finalOS, flags = {}) => {
    if (!flags.isInitial) {
      setIsLoading(true);
    }
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/request-sdk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          os: finalOS,
          productType,
          ...flags
        })
      });

      if (response.ok) {
        if (!flags.isInitial) {
          if (productType === 'apex') {
            localStorage.setItem('contactFormSubmitted', 'true');
            navigate('/apex/demo', { replace: true });
          } else {
            setIsSubmitted(true);
            setShowSuccessModal(true);
          }
        }
      } else {
        if (!flags.isInitial) {
          alert('There was an issue submitting your request. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      if (!flags.isInitial) {
        alert('There was an issue submitting your request. Please try again.');
      }
    } finally {
      if (!flags.isInitial) {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="download-page">
      <div className="download-header">
        <h1>Download Request Form</h1>
        <p>Please fill out the form below to access Tytonvoice SDK downloads.</p>

        <div className="os-available-graphic">
          <span className="os-label">Pryme Currently Available On:</span>
          <div className="os-icons">
            <span className="os-badge"><img src="/icons/ondevice/android.png" alt="Android" className="os-icon-img" />Android</span>
            <span className="os-badge"><img src="/icons/ondevice/linux.png" alt="Linux" className="os-icon-img" />Linux</span>
            <span className="os-badge"><img src="/icons/ondevice/raspberry-pi.png" alt="PiOS" className="os-icon-img" />PiOS</span>
            <span className="os-badge"><img src="/icons/ondevice/macos.png" alt="macOS" className="os-icon-img" />macOS</span>
          </div>
        </div>
      </div>

      <div className="download-content">
        {!isSubmitted ? (
          <form className="download-form">
            <div className="form-group">
              <label htmlFor="fullName">Full Name</label>
              <input type="text" id="fullName" name="fullName" required value={formData.fullName} onChange={handleInputChange} />
            </div>

            <div className="form-group">
              <label htmlFor="email">Business Email Address</label>
              <input type="email" id="email" name="email" required value={formData.email} onChange={handleInputChange} />
            </div>

            <div className="form-group">
              <label htmlFor="company">Organization / Company Name</label>
              <input type="text" id="company" name="company" required value={formData.company} onChange={handleInputChange} />
            </div>

            <div className="form-group">
              <label htmlFor="address">Corporate Address</label>
              <input type="text" id="address" name="address" required value={formData.address} onChange={handleInputChange} />
            </div>

            <div className="form-group">
              <label htmlFor="os">Target Operating System</label>
              <select id="os" name="os" required value={formData.os} onChange={handleInputChange}>
                <option value="" disabled>Select Operating System</option>
                {osOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            {formData.os === 'Other' && (
              <div className="form-group">
                <label htmlFor="osOther">Please specify</label>
                <input type="text" id="osOther" name="osOther" required value={formData.osOther} onChange={handleInputChange} placeholder="E.g. QNX, RTOS, Custom" />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="useCase">Intended Use Case / Application Details</label>
              <textarea
                id="useCase"
                name="useCase"
                rows="4"
                required
                value={formData.useCase}
                onChange={handleInputChange}
                placeholder="Explain how you plan to deploy the ZSL/FSL speech models"
              ></textarea>
            </div>

            <div className="form-group">
              <label htmlFor="timeline">Project Timeline / Deployment Urgency</label>
              <select id="timeline" name="timeline" required value={formData.timeline} onChange={handleInputChange}>
                <option value="" disabled>Select Timeline</option>
                <option value="Immediate (Within 30 days)">Immediate (Within 30 days)</option>
                <option value="Short-term (1-3 months)">Short-term (1-3 months)</option>
                <option value="Planning Phase (3+ months)">Planning Phase (3+ months)</option>
                <option value="Research & Evaluation">Research & Evaluation</option>
              </select>
            </div>

            <div className="form-group captcha-group">
              <ReCAPTCHA
                sitekey="6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"
                onChange={(token) => setCaptchaToken(token)}
              />
            </div>

            <div className="form-submit-group">
              <button type="button" onClick={(e) => handleSubmit(e, 'pryme')} className="submit-btn primary">Request Pryme SDK</button>
              <button type="button" onClick={(e) => handleSubmit(e, 'apex')} className="submit-btn secondary">Try Apex Now</button>
            </div>
          </form>
        ) : (
          <div className="download-success-simple">
            <h2>Request Form Complete</h2>
            <p>Thank you! Your request has been successfully submitted. We'll be in touch soon with additional information. </p>
          </div>
        )}
      </div>

      {showModal && (
        <div className="os-modal-overlay">
          <div className="os-modal">
            <h3>Oh no!</h3>
            <p>It appears your selected OS isn't readily available for Pryme just yet. Select another OS:</p>
            <select value={modalOS} onChange={(e) => setModalOS(e.target.value)} className="modal-select">
              <option value="" disabled>Select Operating System</option>
              {supportedOSOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <div className="modal-actions">
              <button onClick={() => setShowModal(false)} className="cta-button secondary">Cancel</button>
              <button onClick={handleModalSubmit} className="cta-button primary">Submit</button>
            </div>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div className="success-modal-overlay">
          <div className="success-modal">
            <h2>Thank you, {formData.fullName.split(' ')[0]}!</h2>
            <p>Your request for the <strong>Pryme SDK</strong> has been received.</p>

            <div className="email-sent-box">
              <span className="email-icon">✉️</span>
              <h3>Check your inbox</h3>
              <p>We've sent a secure, time-limited link to <strong>{formData.email}</strong>.</p>
            </div>

            <div className="modal-actions" style={{ marginTop: '2rem' }}>
              <button onClick={() => setShowSuccessModal(false)} className="cta-button primary">Close</button>
            </div>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
}

export default Download;
